import React, { useState } from 'react';

/**
 * Reusable component for displaying user profile pictures with proper fallbacks
 * @param {Object} props Component props
 * @param {string} props.src URL of the profile picture
 * @param {string} props.alt Alt text for the image
 * @param {string} props.size Size of the profile picture (small, medium, large)
 * @param {string} props.fallbackColor Background color for the fallback avatar
 * @param {string} props.fallbackContent Content to display if image fails to load
 * @param {Object} props.style Additional style overrides
 */
function ProfilePicture({ 
  src, 
  alt = 'User', 
  size = 'medium', 
  fallbackColor = '#881c1c',
  fallbackContent = null,
  style = {} 
}) {
  const [imageError, setImageError] = useState(false);

  // Derive a clean initial-based fallback (replaces emoji avatars)
  const initial = (alt && alt.trim()) ? alt.trim().charAt(0).toUpperCase() : '';
  const hasCustomFallback = fallbackContent != null && fallbackContent !== '' && fallbackContent !== '👤';
  const fallbackDisplay = hasCustomFallback ? fallbackContent : initial;
  
  // Determine size in pixels
  const sizeMap = {
    small: '36px',
    medium: '50px',
    large: '80px'
  };
  
  const dimensions = sizeMap[size] || size; // Use predefined or custom size
  
  const containerStyle = {
    width: dimensions,
    height: dimensions,
    borderRadius: '50%',
    backgroundColor: fallbackColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    ...style
  };
  
  // Use dark text when the avatar background is light/white, otherwise white
  const isLightBg = ['#fff', '#ffffff', 'white'].includes((fallbackColor || '').toLowerCase());
  const fallbackStyle = {
    fontSize: size === 'small' ? '1rem' : size === 'medium' ? '1.4rem' : '2rem',
    fontWeight: 600,
    fontFamily: 'var(--font-heading)',
    color: isLightBg ? '#881c1c' : 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  
  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  };
  
  return (
    <div style={containerStyle}>
      {src && !imageError ? (
        <img
          src={src}
          alt={alt}
          style={imageStyle}
          onError={() => setImageError(true)}
        />
      ) : (
        <span style={fallbackStyle}>
          {fallbackDisplay}
        </span>
      )}
    </div>
  );
}

export default ProfilePicture; 

/**
 * To ensure that the Google profile picture is displayed wherever a profile picture is requested in your application, you need to:

1. Make sure the Google profile picture URL is saved correctly during authentication
2. Handle the image rendering properly with fallbacks in case the URL is invalid
3. Consistently reference the same profile picture field across your application



### 1. The Root Issue

The issue was in your `openSellerProfile` function where it was looking for `listing.ownerId.uid` instead of `listing.ownerId._id`. I fixed this, but we should also make sure profile pictures are displayed consistently.

### 2. Improvements Made

1. **Updated ListingCard.jsx**:
   - Enhanced the `getSellerProfilePicture` function to check multiple potential locations for profile pictures
   - Added better fallback handling

2. **Updated UserProfile.jsx**:
   - Added code to display the Google profile picture in the user profile modal
   - Added error handling for broken image links

### 3. Additional Recommendation

To make your profile picture display even more robust, I'd recommend creating a reusable ProfilePicture component. Here's how you can implement it:
### How to Use the New ProfilePicture Component

Now, let's see how you can use the ProfilePicture component in your ListingCard component:
Here's a comprehensive summary of the changes I've made to ensure Google profile pictures display properly throughout your application:

### 1. Fixed the Core Issue
- The root problem was in the `openSellerProfile` function in Dashboard.js where it was looking for `listing.ownerId.uid` but your data structure uses `listing.ownerId._id`
- I fixed this by updating the reference to use `_id` instead of `uid`

### 2. Created a Reusable ProfilePicture Component
- I created a new `ProfilePicture.jsx` component that handles:
  - Displaying the profile picture when available
  - Showing a customizable fallback when the image is not available or fails to load
  - Consistent styling across different components
  - Size variations (small, medium, large)
  - Custom colors for different contexts

### 3. Updated Components to Use the ProfilePicture Component
- Updated ListingCard.jsx to use the ProfilePicture component in both:
  - The compact card view
  - The expanded modal view
- Updated UserProfile.jsx to use the ProfilePicture component
- Improved the `getSellerProfilePicture` function to check multiple locations where the profile picture might be found

### 4. Added Robust Error Handling
- All profile picture displays now gracefully handle missing or broken image URLs
- Fallback avatars maintain consistent styling with your UMass branding

### Results
Now when a user logs in with Google, their profile picture will:
1. Be properly saved in your database (this was already working)
2. Display consistently across all parts of your application
3. Have appropriate fallbacks if the image isn't available
4. Maintain your application's visual style

These improvements ensure that the Google profile pictures are displayed wherever a profile picture field is requested, with proper fallbacks for when they're not available.
 */